import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CheckCircle,
  ClipboardList,
  Clock,
  FileText,
  MapPin,
  Shield,
  Users,
} from 'lucide-react';
import StatCard from './StatCard';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';
import {
  complaintService,
  fiscalizacaoService,
  identityService,
  licenseService,
  meService,
  processService,
} from '../../services/api';
import { fetchAllFromGetList, fetchAllPages } from '../../utils/apiPagination';
import { countComplaintGroups } from '../../utils/complaintKpis';
import SlaBadge from '../Common/SlaBadge';
import QueryErrorBanner from '../Common/QueryErrorBanner';

export const PROFILE_VIEW_LABELS = {
  gestor: 'Visualização de perfil: Gestor ambiental',
  analista: 'Visualização de perfil: Analista técnico',
  consulta: 'Visualização de perfil: Consulta (somente leitura)',
  denuncias: 'Visualização de perfil: Triagem de denúncias',
  fiscal: 'Visualização de perfil: Agente fiscal de campo',
  coordenador: 'Visualização de perfil: Coordenação de carteira',
  consultor: 'Visualização de perfil: Consultor técnico',
  requerente: 'Visualização de perfil: Portal do requerente',
};

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

function unwrapTasks(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function chartPairs(chart) {
  const labels = chart?.labels || [];
  const values = chart?.datasets?.[0]?.data || [];
  return labels.map((name, i) => ({ name, count: Number(values[i] || 0) }));
}

function sumChart(chart) {
  return chartPairs(chart).reduce((acc, row) => acc + row.count, 0);
}

function isThisMonth(value) {
  if (!value) return false;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}

function isOpenVistoria(row) {
  const status = String(row?.status || '').toLowerCase();
  return !status || ['aberta', 'em_andamento', 'agendada', 'pendente'].includes(status);
}

function ProgressRow({ label, value, max, hint }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <p className="font-medium text-gray-800">{label}</p>
        <p className="text-xs font-semibold text-gray-600">{pct}%</p>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
      </div>
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}

function TableCard({ title, action, columns, rows, empty }) {
  return (
    <div className="data-list-card overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {action}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {columns.map((col) => (
                <th key={col} className="px-5 py-3">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-5 py-8 text-center text-slate-500">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  {row.cells.map((cell, idx) => (
                    <td key={`${row.id}-${idx}`} className="px-5 py-3 text-slate-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ProfileBanner({ profile }) {
  const { tenant } = useTenant();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm">
      <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
      <span className="font-medium text-gray-800">{PROFILE_VIEW_LABELS[profile] || PROFILE_VIEW_LABELS.gestor}</span>
      {tenant?.name ? <span className="text-gray-500">— {tenant.name}</span> : null}
    </div>
  );
}

export function OrgKpis({ insights }) {
  const { data: tasks } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data));
  const { data: complaints = [] } = useQuery(['dash-complaints'], () =>
    fetchAllFromGetList(complaintService.complaintCrud.getList)
  );
  const { data: vistorias = [] } = useQuery(['dash-vistorias'], () =>
    fetchAllPages((params) => fiscalizacaoService.listVistorias(params))
  );
  const groups = countComplaintGroups(complaints);
  const monthVistorias = vistorias.filter((row) => isThisMonth(row.created_at || row.data)).length;
  const critical = (tasks?.summary?.vencido || 0) + (tasks?.summary?.atencao || 0);
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Total de processos"
        value={sumChart(insights?.process_funnel)}
        hint="Sob custódia do órgão"
        icon={FileText}
        color="primary"
      />
      <StatCard
        title="Fiscalizações do mês"
        value={monthVistorias}
        hint="Vistorias registradas no ciclo"
        icon={Shield}
        color="success"
      />
      <StatCard
        title="Denúncias ativas"
        value={groups.active}
        hint="Necessitam de triagem ou acompanhamento"
        icon={AlertTriangle}
        color="warning"
      />
      <StatCard
        title="Prazos críticos"
        value={critical}
        hint="Vencidos ou em atenção"
        icon={Clock}
        color="danger"
      />
    </div>
  );
}

export function FieldProgress({ insights }) {
  const { data: vistorias = [] } = useQuery(['dash-vistorias'], () =>
    fetchAllPages((params) => fiscalizacaoService.listVistorias(params))
  );
  const { data: complaints = [] } = useQuery(['dash-complaints'], () =>
    fetchAllFromGetList(complaintService.complaintCrud.getList)
  );
  const month = vistorias.filter((row) => isThisMonth(row.created_at || row.data));
  const done = month.filter((row) => !isOpenVistoria(row)).length;
  const groups = countComplaintGroups(complaints);
  const slaDays = insights?.lead_time?.avg_days ?? 0;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm font-semibold text-gray-900">Ação e fiscalização de campo</p>
        <div className="mt-4 space-y-4">
          <ProgressRow
            label="Meta de inspeções do mês"
            value={done}
            max={Math.max(month.length, 1)}
            hint={`${done} de ${Math.max(month.length, 0)} concluídas`}
          />
          <ProgressRow
            label="Resolução de denúncias (SLA)"
            value={groups.resolved}
            max={Math.max(groups.total, 1)}
            hint={`SLA médio de ${slaDays} dia(s)`}
          />
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm font-semibold text-gray-900">Informativos de campo</p>
        <p className="mt-3 text-sm text-gray-600">
          {groups.active
            ? `${groups.active} denúncia(s) ativa(s) e ${month.length} vistoria(s) no mês — priorize as ocorrências em atenção.`
            : 'Nenhuma ocorrência ativa no recorte atual.'}
        </p>
        <Link to="/inspections" className="mt-4 inline-block text-sm font-medium text-primary-700">
          Abrir fiscalização
        </Link>
      </div>
    </div>
  );
}

export function PendingDispatches() {
  const { data, error } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data));
  const tasks = unwrapTasks(data).slice(0, 6);
  if (error) return <QueryErrorBanner error={error} />;
  return (
    <TableCard
      title="Tarefas e despachos pendentes"
      action={
        <Link to="/me" className="text-sm font-medium text-primary-700">
          Ver fila
        </Link>
      }
      columns={['Tipo', 'Tarefa', 'Prazo', 'Ação']}
      empty="Nenhuma pendência atribuída."
      rows={tasks.map((item) => ({
        id: `${item.kind}-${item.id}`,
        cells: [
          item.kind,
          <div key="t">
            <p className="font-medium text-slate-900">{item.titulo}</p>
            <p className="text-xs text-slate-500">{item.subtitulo}</p>
          </div>,
          <SlaBadge key="s" sla={item.sla} diasRestantes={item.dias_restantes} prazo={item.prazo} />,
          item.link_href ? (
            <Link key="a" to={item.link_href} className="font-medium text-primary-700">
              Abrir
            </Link>
          ) : (
            '—'
          ),
        ],
      }))}
    />
  );
}

export function AnalystKpis({ insights }) {
  const { data: tasks } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data));
  const summary = tasks?.summary || {};
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard title="Minha fila" value={summary.total ?? 0} hint="Processos aguardando análise" icon={ClipboardList} />
      <StatCard
        title="No prazo"
        value={summary.no_prazo ?? 0}
        hint="Análises em andamento"
        icon={CheckCircle}
        color="success"
      />
      <StatCard
        title="Pendências externas"
        value={summary.atencao ?? 0}
        hint="Aguardando documento ou retorno"
        icon={Clock}
        color="warning"
      />
      <StatCard
        title="Tempo médio"
        displayValue={`${insights?.lead_time?.avg_days ?? 0}d`}
        hint="Por análise emitida"
        icon={FileText}
      />
    </div>
  );
}

export function AnalystQueue() {
  const { data } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data));
  const tasks = unwrapTasks(data).slice(0, 8);
  return (
    <TableCard
      title="Minha fila de trabalho ativa"
      action={
        <Link to="/me" className="text-sm font-medium text-primary-700">
          Analisar
        </Link>
      }
      columns={['Tipo', 'Empreendimento / tarefa', 'Prazo', 'Ação']}
      empty="Fila vazia."
      rows={tasks.map((item) => ({
        id: `${item.kind}-${item.id}`,
        cells: [
          item.kind,
          item.titulo || item.subtitulo || '—',
          <SlaBadge key="s" sla={item.sla} diasRestantes={item.dias_restantes} prazo={item.prazo} />,
          item.link_href ? (
            <Link key="a" to={item.link_href} className="font-medium text-primary-700">
              Analisar
            </Link>
          ) : (
            '—'
          ),
        ],
      }))}
    />
  );
}

export function ComplaintKpis() {
  const { data: complaints = [] } = useQuery(['dash-complaints'], () =>
    fetchAllFromGetList(complaintService.complaintCrud.getList)
  );
  const groups = countComplaintGroups(complaints);
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard title="Denúncias recebidas" value={groups.total} hint="No cadastro do órgão" icon={AlertTriangle} />
      <StatCard
        title="Triadas / direcionadas"
        value={groups.forwarded}
        hint="Para a fiscalização"
        icon={Shield}
        color="warning"
      />
      <StatCard
        title="Aguardando triagem"
        value={groups.awaitingTriage}
        hint="Ação urgente"
        icon={Clock}
        color="danger"
      />
      <StatCard title="Resolvidas" value={groups.resolved} hint="Casos encerrados" icon={CheckCircle} color="success" />
    </div>
  );
}

export function ComplaintMix({ insights }) {
  const categories = chartPairs(insights?.complaints_by_category).slice(0, 4);
  const max = Math.max(1, ...categories.map((row) => row.count));
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm font-semibold text-gray-900">Categorias de ocorrência</p>
        <div className="mt-4 space-y-3">
          {categories.length === 0 ? (
            <p className="text-sm text-gray-500">Sem denúncias no período.</p>
          ) : (
            categories.map((row) => (
              <ProgressRow
                key={row.name}
                label={row.name}
                value={row.count}
                max={max}
                hint={`${row.count} registro(s)`}
              />
            ))
          )}
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-sm font-semibold text-gray-900">Denúncias por bairro</p>
        <ul className="mt-4 space-y-2">
          {chartPairs(insights?.complaints_by_bairro)
            .slice(0, 5)
            .map((row) => (
              <li key={row.name} className="flex items-center justify-between text-sm">
                <span className="truncate pr-3 text-gray-700">{row.name}</span>
                <span className="font-semibold text-gray-900">{row.count}</span>
              </li>
            ))}
        </ul>
        <Link to="/complaint/complaints" className="mt-4 inline-block text-sm font-medium text-primary-700">
          Abrir denúncias
        </Link>
      </div>
    </div>
  );
}

export function RecentComplaints() {
  const { data: complaints = [] } = useQuery(['dash-complaints'], () =>
    fetchAllFromGetList(complaintService.complaintCrud.getList)
  );
  const { data: categoriesRaw } = useQuery(['dash-complaint-cats'], () =>
    complaintService.categoryCrud.getList({ limit: 200 }).then((r) => r.data)
  );
  const categories = unwrap(categoriesRaw);
  const rows = [...complaints]
    .sort((a, b) => String(b.data || b.created_at || '').localeCompare(String(a.data || a.created_at || '')))
    .slice(0, 6);
  return (
    <TableCard
      title="Denúncias recentes de ouvidoria"
      action={
        <Link to="/complaint/complaints" className="text-sm font-medium text-primary-700">
          Ver todas
        </Link>
      }
      columns={['Código', 'Ocorrência', 'Categoria', 'Status']}
      empty="Nenhuma denúncia recente."
      rows={rows.map((row) => ({
        id: row.id,
        cells: [
          row.numero || String(row.id || '').slice(0, 8),
          <span key="d" className="line-clamp-2">
            {row.descricao || row.local || '—'}
          </span>,
          categories.find((cat) => cat.id === row.category_id)?.name || '—',
          String(row.status || '—'),
        ],
      }))}
    />
  );
}

export function InspectionKpis() {
  const { data: vistorias = [] } = useQuery(['dash-vistorias'], () =>
    fetchAllPages((params) => fiscalizacaoService.listVistorias(params))
  );
  const { data: tasks } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data));
  const open = vistorias.filter(isOpenVistoria);
  const month = vistorias.filter((row) => isThisMonth(row.created_at || row.data));
  const doneMonth = month.filter((row) => !isOpenVistoria(row)).length;
  const pct = month.length ? Math.round((doneMonth / month.length) * 100) : 0;
  const alerts = (tasks?.summary?.atencao || 0) + (tasks?.summary?.vencido || 0);
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard title="Ordens de vistoria" value={open.length} hint="Em aberto" icon={ClipboardList} />
      <StatCard
        title="Vistorias efetuadas"
        value={doneMonth}
        hint="Neste ciclo mensal"
        icon={CheckCircle}
        color="success"
      />
      <StatCard title="Alertas de área" value={alerts} hint="Regiões de atenção" icon={MapPin} color="warning" />
      <StatCard title="Ordens concluídas" displayValue={`${pct}%`} hint="Índice de resolução" icon={Shield} color="success" />
    </div>
  );
}

export function InspectionQueue() {
  const { data: vistorias = [] } = useQuery(['dash-vistorias'], () =>
    fetchAllPages((params) => fiscalizacaoService.listVistorias(params))
  );
  const rows = [...vistorias].slice(0, 6);
  return (
    <TableCard
      title="Minhas vistorias agendadas"
      action={
        <Link to="/inspections" className="text-sm font-medium text-primary-700">
          Iniciar checklist
        </Link>
      }
      columns={['Ordem', 'Alvo', 'Tipo', 'Status']}
      empty="Nenhuma vistoria agendada."
      rows={rows.map((row) => ({
        id: row.id,
        cells: [
          row.numero || String(row.id || '').slice(0, 8),
          row.local || row.endereco || row.complaint_id || '—',
          row.license_tipo || row.tipo || 'Campo',
          row.status || 'aberta',
        ],
      }))}
    />
  );
}

export function PortfolioKpis({ insights }) {
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const { data: clients = [] } = useQuery(['dash-clients'], () =>
    fetchAllPages((params) => identityService.getClients(params))
  );
  const { data: licenses = [] } = useQuery(['dash-licenses-portfolio'], () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const { data: processes = [] } = useQuery(['dash-processos'], () =>
    fetchAllPages((params) => processService.getProcessos(params))
  );
  const pending = licenses.filter((row) => ['1', '2', '4'].includes(String(row.status)));
  const sla = processes.filter((row) => [1, 2].includes(Number(row.situacao))).length;
  const homolog = licenses.filter((row) => ['3', '5'].includes(String(row.status))).length;
  const pct = licenses.length ? Math.round((homolog / licenses.length) * 1000) / 10 : 0;
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title={party.consultoria ? 'Carteira ativa' : 'Requerentes'}
        value={clients.length}
        hint={party.consultoria ? 'Clientes sob gestão' : 'Requerentes cadastrados'}
        icon={Users}
      />
      <StatCard
        title="Processos do órgão"
        value={sumChart(insights?.process_funnel) || processes.length}
        hint={`${pending.length} licença(s) aguardando`}
        icon={Briefcase}
      />
      <StatCard title="SLA próximo ao limite" value={sla} hint="Processos em andamento" icon={Clock} color="warning" />
      <StatCard title="Taxa de homologação" displayValue={`${pct}%`} hint="Licenças concluídas" icon={CheckCircle} color="success" />
    </div>
  );
}

export function TeamPerformance({ insights }) {
  const rows = (insights?.productivity || []).slice(0, 8);
  return (
    <TableCard
      title="Desempenho da equipe e alocação"
      action={
        <Link to="/portfolio" className="text-sm font-medium text-primary-700">
          Distribuir processo
        </Link>
      }
      columns={['Consultor / servidor', 'Concluídas', 'Status']}
      empty="Sem produtividade no período."
      rows={rows.map((row) => ({
        id: row.servidor,
        cells: [row.servidor, row.concluidas ?? row.count ?? 0, Number(row.concluidas || 0) >= 10 ? 'Estável' : 'Em carga'],
      }))}
    />
  );
}

export function MonitoredEnterprises() {
  const { data: enterprises = [] } = useQuery(['dash-enterprises'], () =>
    fetchAllPages((params) => identityService.getEnterprises(params))
  );
  const { data: licenses = [] } = useQuery(['dash-licenses-map'], () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const rows = useMemo(() => {
    return enterprises.slice(0, 6).map((ent) => {
      const mine = licenses.filter((lic) => String(lic.empreendimento_id) === String(ent.id));
      const latest = mine[0];
      return {
        id: ent.id,
        cells: [
          latest?.numero || String(ent.id || '').slice(0, 8),
          ent.name || ent.nome || '—',
          latest?.tipo_nome || latest?.tipo || '—',
          latest?.validade ? String(latest.validade).slice(0, 10) : '—',
          latest ? String(latest.status || '—') : 'Sem licença',
        ],
      };
    });
  }, [enterprises, licenses]);
  return (
    <TableCard
      title="Empreendimentos monitorados"
      action={
        <Link to="/identity" className="text-sm font-medium text-primary-700">
          Ver cadastro
        </Link>
      }
      columns={['Código', 'Empreendimento', 'Tipo', 'Vencimento', 'Status']}
      empty="Nenhum empreendimento cadastrado."
      rows={rows}
    />
  );
}

export function LicenseCategoryBars({ licensesByStatus }) {
  const pairs = chartPairs(licensesByStatus);
  const max = Math.max(1, ...pairs.map((row) => row.count));
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm font-semibold text-gray-900">Licenças por status</p>
      <div className="mt-4 space-y-3">
        {pairs.length === 0 ? (
          <p className="text-sm text-gray-500">Sem dados.</p>
        ) : (
          pairs.map((row) => (
            <ProgressRow key={row.name} label={row.name} value={row.count} max={max} hint={`${row.count}`} />
          ))
        )}
      </div>
    </div>
  );
}

export function ConsultationKpis({ stats }) {
  const { data: enterprises = [] } = useQuery(['dash-enterprises'], () =>
    fetchAllPages((params) => identityService.getEnterprises(params))
  );
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      <StatCard title="Total de empreendimentos" value={enterprises.length} hint="Registrados na base" icon={Building2} />
      <StatCard title="Vigentes" value={stats?.activeLicenses ?? 0} hint="Licenciamento ativo" icon={CheckCircle} color="success" />
      <StatCard title="Em trâmite" value={stats?.pendingLicenses ?? 0} hint="Análise em andamento" icon={Clock} color="warning" />
      <StatCard title="Irregulares" value={stats?.expiredLicenses ?? 0} hint="Ação requerida de campo" icon={AlertTriangle} color="danger" />
    </div>
  );
}
